import { Check as CheckIcon, Close as CloseIcon, Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Drawer,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { useCallback, useEffect, useMemo, useState } from "react";

import { makeProfileAvatarUrl } from "~utils/url";

import { AddEventTimesButton } from "../../molecules/AddEventTimesButton/AddEventTimesButton";
import { ConfirmDeleteDialog } from "../../molecules/ConfirmDeleteDialog/ConfirmDeleteDialog";
import { AddEventTimesDialog } from "../AddEventTimesModal/AddEventTimesDialog";
import { successGradientBackground, successGradientButtonSx } from "../AddEventTimesModal/AddEventTimesModal.theme";
import { type MeetingTimeData } from "../AddEventTimesModal/AddEventTimesModal.types";

// User-facing strings
const STRINGS = {
  ATTENDEES: "Attendees",
  ATTENDEES_WILL_BE_NOTIFIED: (count: number) =>
    `${count} attendee${count === 1 ? "" : "s"} will be notified that their RSVP was cancelled.`,
  ATTENDING: "Attending",
  CANCEL: "Cancel",
  CANCEL_RSVP_TOOLTIP: "Click to cancel RSVP",
  CONFIRM_DELETE: (count: number) => `Confirm Delete (${count})`,
  DELETE_EVENT_TIMES: "Delete Event Times",
  DELETE_TOOLTIP: "Delete Meeting Time",
  EDIT: "Edit",
  EDIT_CAPACITY: "Edit capacity",
  EDIT_DATE_TIME: "Edit Date & Time",
  EVENT_TIMES_SELECTED: (count: number) => `${count} event time${count === 1 ? "" : "s"} selected`,
  NO_SERIES: "Not part of a series",
  PART_OF_SERIES: "Part of series:",
  RSVP: "RSVP",
  SAVE: "Save",
  SELECT_ALL: "Select All",
  SELECT_SERIES: "Select series",
  SPOTS_AVAILABLE: "Spots Available",
  TITLE: "Upcoming Event Times",
  UNLIMITED: "Unlimited",
  UNLIMITED_SPOTS: "Unlimited Spots Available",
} as const;

interface EventTime {
  id: string;
  beginMeetingTime: string;
  endMeetingTime: string;
  userHasRsvpd: boolean;
  capacity: number;
  seriesId?: string;
  series?: {
    id: string;
    name: string;
    description: string;
  };
  attendees: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    isVetted: boolean;
  }[];
}

interface EventTimesSlideoutProps {
  open: boolean;
  onClose: () => void;
  eventTimes: EventTime[];
  onRSVP: (meetingTimeId: string) => void;
  onCancelRSVP: (meetingTimeId: string) => void;
  onDeleteMeetingTime?: (meetingTimeId: string) => void;
  onBulkDeleteMeetingTimes?: (meetingTimeIds: string[]) => void | Promise<void>;
  onUpdateMeetingTime?: (
    meetingTimeId: string,
    updates: {
      seriesId?: string | null;
      capacity?: number | null;
      beginTime?: string;
      endTime?: string;
    },
  ) => void;
  onCreateMeetingTimes?: (meetingTimes: MeetingTimeData[], eventId: string) => void;
  isCreatingMeetingTimes?: boolean;
  isDeletingMeetingTimes?: boolean;
  eventId?: string;
  availableSeries?: { id: string; name: string; description: string }[];
  canManage?: boolean;
}

export const EventTimesSlideout = ({
  open,
  onClose,
  eventTimes,
  onRSVP,
  onCancelRSVP,
  onDeleteMeetingTime,
  onBulkDeleteMeetingTimes,
  onUpdateMeetingTime,
  onCreateMeetingTimes,
  isCreatingMeetingTimes = false,
  isDeletingMeetingTimes = false,
  eventId,
  availableSeries = [],
  canManage = false,
}: EventTimesSlideoutProps) => {
  // Track modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Track which cards are being edited
  const [editingCards, setEditingCards] = useState<Record<string, boolean>>({});
  // Track all pending changes in a single state object
  const [pendingChanges, setPendingChanges] = useState<
    Record<
      string,
      {
        capacity?: number | null;
        beginTime?: string;
        endTime?: string;
        seriesId?: string | null;
      }
    >
  >({});

  // Track validation errors
  const [validationErrors, setValidationErrors] = useState<Record<string, { endTimeBeforeStart?: boolean }>>({});

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setIsSelectionMode(false);
      setSelectedIds([]);
      setDeleteDialogOpen(false);
    }
  }, [open]);

  const formatEventTimeLabel = useCallback((beginMeetingTime: string, endMeetingTime: string) => {
    const startTime = DateTime.fromISO(beginMeetingTime).setZone("local");
    const endTime = DateTime.fromISO(endMeetingTime).setZone("local");
    return `${startTime.toFormat("EEEE, MMM d")} · ${startTime.toFormat("h:mm")} - ${endTime.toFormat("h:mm a")}`;
  }, []);

  const toggleSelection = useCallback((eventTimeId: string) => {
    setSelectedIds((prev) =>
      prev.includes(eventTimeId) ? prev.filter((id) => id !== eventTimeId) : [...prev, eventTimeId],
    );
  }, []);

  const handleExitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    setSelectedIds([]);
    setDeleteDialogOpen(false);
  }, []);

  const selectedEventTimes = useMemo(
    () => eventTimes.filter((eventTime) => selectedIds.includes(eventTime.id)),
    [eventTimes, selectedIds],
  );

  const selectedLabels = useMemo(
    () =>
      selectedEventTimes.map((eventTime) => formatEventTimeLabel(eventTime.beginMeetingTime, eventTime.endMeetingTime)),
    [formatEventTimeLabel, selectedEventTimes],
  );

  const selectedAttendeeCount = useMemo(
    () => selectedEventTimes.reduce((total, eventTime) => total + eventTime.attendees.length, 0),
    [selectedEventTimes],
  );

  const handleConfirmBulkDelete = useCallback(async () => {
    if (!onBulkDeleteMeetingTimes || selectedIds.length === 0) {
      return;
    }

    try {
      await onBulkDeleteMeetingTimes(selectedIds);
      handleExitSelectionMode();
    } catch {
      // Error toast is handled by the caller
    }
  }, [handleExitSelectionMode, onBulkDeleteMeetingTimes, selectedIds]);

  // Validate time changes
  const validateTimes = useCallback(
    (meetingTimeId: string, beginTime?: string, endTime?: string) => {
      const start = beginTime || eventTimes.find((et) => et.id === meetingTimeId)?.beginMeetingTime;
      const end = endTime || eventTimes.find((et) => et.id === meetingTimeId)?.endMeetingTime;

      if (start && end) {
        const startTime = DateTime.fromISO(start);
        const endTime = DateTime.fromISO(end);
        const isEndBeforeStart = endTime < startTime;

        setValidationErrors((prev) => ({
          ...prev,
          [meetingTimeId]: { ...prev[meetingTimeId], endTimeBeforeStart: isEndBeforeStart },
        }));

        return !isEndBeforeStart;
      }

      return true;
    },
    [eventTimes],
  );

  // Sort event times by date
  const sortedEventTimes = [...eventTimes].sort((a, b) => {
    const dateA = DateTime.fromISO(a.beginMeetingTime);
    const dateB = DateTime.fromISO(b.beginMeetingTime);
    return dateA.toMillis() - dateB.toMillis();
  });

  const allEventTimeIds = useMemo(() => sortedEventTimes.map((eventTime) => eventTime.id), [sortedEventTimes]);
  const isAllSelected =
    allEventTimeIds.length > 0 && allEventTimeIds.every((eventTimeId) => selectedIds.includes(eventTimeId));
  const isSomeSelected = selectedIds.length > 0 && !isAllSelected;

  const handleSelectAll = useCallback(() => {
    setSelectedIds(isAllSelected ? [] : allEventTimeIds);
  }, [allEventTimeIds, isAllSelected]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: {
              sm: 400,
              xs: "100%",
            },
          },
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
        <Box
          sx={{
            alignItems: "center",
            background: successGradientBackground,
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            p: 3,
          }}
        >
          <Typography variant="h6">{STRINGS.TITLE}</Typography>
          <IconButton
            onClick={onClose}
            aria-label="Close event times"
            sx={{
              "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
              color: "white",
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Add / delete event times actions */}
        {canManage && onCreateMeetingTimes && eventId && (
          <Box sx={{ borderBottom: "1px solid", borderColor: "divider", p: 2 }}>
            {!isSelectionMode ? (
              <Stack spacing={1.5}>
                <AddEventTimesButton onClick={() => setIsModalOpen(true)} disabled={isCreatingMeetingTimes} />
                {onBulkDeleteMeetingTimes && sortedEventTimes.length > 0 && (
                  <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    onClick={() => setIsSelectionMode(true)}
                    disabled={isCreatingMeetingTimes || isDeletingMeetingTimes}
                  >
                    {STRINGS.DELETE_EVENT_TIMES}
                  </Button>
                )}
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <Typography color="text.secondary" variant="body2">
                  {selectedIds.length > 0
                    ? STRINGS.EVENT_TIMES_SELECTED(selectedIds.length)
                    : "Select event times to delete"}
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={handleExitSelectionMode}
                    disabled={isDeletingMeetingTimes}
                  >
                    {STRINGS.CANCEL}
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    color="error"
                    disabled={selectedIds.length === 0 || isDeletingMeetingTimes}
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    {STRINGS.CONFIRM_DELETE(selectedIds.length)}
                  </Button>
                </Stack>
              </Stack>
            )}
            <AddEventTimesDialog
              open={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              onSubmit={onCreateMeetingTimes}
              eventId={eventId}
              series={availableSeries}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          <Stack spacing={2}>
            {isSelectionMode && sortedEventTimes.length > 0 && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={isSomeSelected}
                    onChange={handleSelectAll}
                    disabled={isDeletingMeetingTimes}
                  />
                }
                label={STRINGS.SELECT_ALL}
              />
            )}
            {sortedEventTimes.map((eventTime) => {
              const startTime = DateTime.fromISO(eventTime.beginMeetingTime).setZone("local");
              const endTime = DateTime.fromISO(eventTime.endMeetingTime).setZone("local");
              const isEventFull = eventTime.capacity > 0 && eventTime.attendees.length >= eventTime.capacity;
              const progress = eventTime.capacity > 0 ? (eventTime.attendees.length / eventTime.capacity) * 100 : 0;
              const seatsAvailable = eventTime.capacity > 0 ? eventTime.capacity - eventTime.attendees.length : 0;

              return (
                <Card key={eventTime.id}>
                  <CardHeader
                    avatar={
                      isSelectionMode ? (
                        <Checkbox
                          checked={selectedIds.includes(eventTime.id)}
                          onChange={() => toggleSelection(eventTime.id)}
                          disabled={isDeletingMeetingTimes}
                          inputProps={{
                            "aria-label": `Select ${formatEventTimeLabel(eventTime.beginMeetingTime, eventTime.endMeetingTime)}`,
                          }}
                        />
                      ) : undefined
                    }
                    title={
                      <Stack>
                        <Typography variant="subtitle1">{startTime.toFormat("EEEE, MMM d")}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {startTime.toFormat("h:mm")} - {endTime.toFormat("h:mm a")}
                        </Typography>
                      </Stack>
                    }
                    action={
                      <Stack direction="row" spacing={1}>
                        {canManage &&
                          !isSelectionMode &&
                          (editingCards[eventTime.id] ? (
                            <>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => {
                                  setPendingChanges((prev) => {
                                    const { [eventTime.id]: _, ...rest } = prev;
                                    return rest;
                                  });
                                  setEditingCards((prev) => ({ ...prev, [eventTime.id]: false }));
                                }}
                              >
                                {STRINGS.CANCEL}
                              </Button>
                              <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                disabled={validationErrors[eventTime.id]?.endTimeBeforeStart}
                                onClick={() => {
                                  if (!onUpdateMeetingTime) {
                                    return;
                                  }

                                  const updates = pendingChanges[eventTime.id] || {};
                                  onUpdateMeetingTime(eventTime.id, updates);

                                  // Clear pending changes
                                  setPendingChanges((prev) => {
                                    const { [eventTime.id]: _, ...rest } = prev;
                                    return rest;
                                  });
                                  setEditingCards((prev) => ({ ...prev, [eventTime.id]: false }));
                                }}
                              >
                                {STRINGS.SAVE}
                              </Button>
                            </>
                          ) : (
                            <>
                              <IconButton
                                size="small"
                                onClick={() => setEditingCards((prev) => ({ ...prev, [eventTime.id]: true }))}
                              >
                                <EditIcon />
                              </IconButton>
                              {onDeleteMeetingTime && (
                                <Tooltip title={STRINGS.DELETE_TOOLTIP}>
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onDeleteMeetingTime(eventTime.id)}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </>
                          ))}
                      </Stack>
                    }
                  />
                  <CardContent>
                    <Stack spacing={2}>
                      <Box>
                        {canManage && editingCards[eventTime.id] ? (
                          <>
                            <Box sx={{ mb: 2 }}>
                              <TextField
                                type="date"
                                size="small"
                                fullWidth
                                label={STRINGS.EDIT_DATE_TIME}
                                value={
                                  pendingChanges[eventTime.id]?.beginTime?.split("T")[0] ||
                                  startTime.toFormat("yyyy-MM-dd")
                                }
                                onChange={(e) => {
                                  const newDate = e.target.value;
                                  const currentChanges = pendingChanges[eventTime.id] || {};
                                  const currentBeginTime = DateTime.fromISO(
                                    currentChanges.beginTime || eventTime.beginMeetingTime,
                                  );
                                  const currentEndTime = DateTime.fromISO(
                                    currentChanges.endTime || eventTime.endMeetingTime,
                                  );
                                  const newDateTime = DateTime.fromISO(newDate, { zone: "local" });

                                  const newBeginTime = currentBeginTime.setZone("local").set({
                                    day: newDateTime.day,
                                    month: newDateTime.month,
                                    year: newDateTime.year,
                                  });

                                  const newEndTime = currentEndTime.setZone("local").set({
                                    day: newDateTime.day,
                                    month: newDateTime.month,
                                    year: newDateTime.year,
                                  });

                                  setPendingChanges((prev) => ({
                                    ...prev,
                                    [eventTime.id]: {
                                      ...prev[eventTime.id],
                                      beginTime: newBeginTime.toISO() || eventTime.beginMeetingTime,
                                      endTime: newEndTime.toISO() || eventTime.endMeetingTime,
                                    },
                                  }));
                                }}
                                sx={{ mb: 1 }}
                                slotProps={{
                                  htmlInput: { min: DateTime.now().toFormat("yyyy-MM-dd") },
                                  inputLabel: { shrink: true },
                                }}
                              />
                              <Stack direction="row" spacing={1}>
                                <TextField
                                  type="time"
                                  size="small"
                                  fullWidth
                                  label="Start Time"
                                  value={
                                    pendingChanges[eventTime.id]?.beginTime?.split("T")[1].split(".")[0] ||
                                    startTime.toFormat("HH:mm")
                                  }
                                  onChange={(e) => {
                                    const newTime = e.target.value;
                                    const currentDate = pendingChanges[eventTime.id]?.beginTime
                                      ? DateTime.fromISO(pendingChanges[eventTime.id].beginTime || "").toFormat(
                                          "yyyy-MM-dd",
                                        )
                                      : startTime.toFormat("yyyy-MM-dd");

                                    const newBeginTime =
                                      DateTime.fromFormat(`${currentDate} ${newTime}`, "yyyy-MM-dd HH:mm", {
                                        zone: "local",
                                      }).toISO() || eventTime.beginMeetingTime;

                                    setPendingChanges((prev) => ({
                                      ...prev,
                                      [eventTime.id]: {
                                        ...prev[eventTime.id],
                                        beginTime: newBeginTime,
                                      },
                                    }));

                                    validateTimes(eventTime.id, newBeginTime, pendingChanges[eventTime.id]?.endTime);
                                  }}
                                  slotProps={{
                                    inputLabel: { shrink: true },
                                  }}
                                />
                                <TextField
                                  type="time"
                                  size="small"
                                  fullWidth
                                  label="End Time"
                                  error={validationErrors[eventTime.id]?.endTimeBeforeStart}
                                  helperText={
                                    validationErrors[eventTime.id]?.endTimeBeforeStart
                                      ? "End time must be after start time"
                                      : undefined
                                  }
                                  value={
                                    pendingChanges[eventTime.id]?.endTime?.split("T")[1].split(".")[0] ||
                                    endTime.toFormat("HH:mm")
                                  }
                                  onChange={(e) => {
                                    const newTime = e.target.value;
                                    const currentDate = pendingChanges[eventTime.id]?.endTime
                                      ? DateTime.fromISO(pendingChanges[eventTime.id].endTime || "").toFormat(
                                          "yyyy-MM-dd",
                                        )
                                      : startTime.toFormat("yyyy-MM-dd");

                                    const newEndTime =
                                      DateTime.fromFormat(`${currentDate} ${newTime}`, "yyyy-MM-dd HH:mm", {
                                        zone: "local",
                                      }).toISO() || eventTime.endMeetingTime;

                                    setPendingChanges((prev) => ({
                                      ...prev,
                                      [eventTime.id]: {
                                        ...prev[eventTime.id],
                                        endTime: newEndTime,
                                      },
                                    }));

                                    validateTimes(eventTime.id, pendingChanges[eventTime.id]?.beginTime, newEndTime);
                                  }}
                                  slotProps={{
                                    inputLabel: { shrink: true },
                                  }}
                                />
                              </Stack>
                            </Box>
                            <FormControl fullWidth size="small">
                              <Select
                                value={
                                  pendingChanges[eventTime.id]?.seriesId === null
                                    ? ""
                                    : (pendingChanges[eventTime.id]?.seriesId ?? eventTime.seriesId?.toString() ?? "")
                                }
                                onChange={(e) => {
                                  const newSeriesId = e.target.value === "" ? null : e.target.value;
                                  setPendingChanges((prev) => ({
                                    ...prev,
                                    [eventTime.id]: {
                                      ...prev[eventTime.id],
                                      seriesId: newSeriesId,
                                    },
                                  }));
                                }}
                                displayEmpty
                              >
                                <MenuItem value="">{STRINGS.NO_SERIES}</MenuItem>
                                {availableSeries.map((series) => (
                                  <MenuItem key={series.id} value={series.id}>
                                    {series.name}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <TextField
                              label={STRINGS.EDIT_CAPACITY}
                              type="number"
                              size="small"
                              value={pendingChanges[eventTime.id]?.capacity ?? (eventTime.capacity || "")}
                              onChange={(e) => {
                                const value = e.target.value;
                                setPendingChanges((prev) => ({
                                  ...prev,
                                  [eventTime.id]: {
                                    ...prev[eventTime.id],
                                    capacity: value === "" ? null : Math.max(0, parseInt(value, 10)),
                                  },
                                }));
                              }}
                              sx={{ mt: 2 }}
                              fullWidth
                              slotProps={{
                                input: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      {(pendingChanges[eventTime.id]?.capacity ?? eventTime.capacity) === 0
                                        ? STRINGS.UNLIMITED
                                        : STRINGS.SPOTS_AVAILABLE}
                                    </InputAdornment>
                                  ),
                                },
                              }}
                            />
                          </>
                        ) : (
                          <>
                            {eventTime.series && (
                              <Typography variant="subtitle2" color="text.secondary">
                                {STRINGS.PART_OF_SERIES} {eventTime.series.name}
                              </Typography>
                            )}
                            <Typography variant="subtitle2">
                              {eventTime.capacity === 0
                                ? STRINGS.UNLIMITED_SPOTS
                                : `${STRINGS.SPOTS_AVAILABLE}: ${seatsAvailable}`}
                            </Typography>
                            {eventTime.capacity > 0 && (
                              <LinearProgress variant="determinate" value={progress} sx={{ borderRadius: 1 }} />
                            )}
                          </>
                        )}
                      </Box>
                      {!isSelectionMode &&
                        !editingCards[eventTime.id] &&
                        (eventTime.userHasRsvpd ? (
                          <Tooltip title={STRINGS.CANCEL_RSVP_TOOLTIP}>
                            <Button
                              variant="outlined"
                              color="success"
                              onClick={() => onCancelRSVP(eventTime.id)}
                              startIcon={<CheckIcon />}
                              size="small"
                              fullWidth
                              sx={{
                                "&:hover": {
                                  backgroundColor: "error.light",
                                  borderColor: "error.main",
                                  color: "error.main",
                                },
                              }}
                            >
                              {STRINGS.ATTENDING}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="contained"
                            onClick={() => onRSVP(eventTime.id)}
                            size="small"
                            disabled={isEventFull}
                            fullWidth
                            sx={successGradientButtonSx}
                          >
                            {STRINGS.RSVP}
                          </Button>
                        ))}
                      <Box>
                        <Typography variant="subtitle2">
                          {STRINGS.ATTENDEES} ({eventTime.attendees.length})
                        </Typography>
                        <Stack spacing={1} sx={{ mt: 1 }}>
                          {eventTime.attendees.map((attendee) => (
                            <Stack key={attendee.id} direction="row" spacing={1} alignItems="center">
                              <Box
                                component="img"
                                src={makeProfileAvatarUrl(attendee.id)}
                                alt={`${attendee.firstName} ${attendee.lastName}`}
                                sx={{
                                  borderRadius: "50%",
                                  height: 32,
                                  width: 32,
                                }}
                              />
                              <Typography>
                                {attendee.firstName} {attendee.lastName}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmBulkDelete}
        itemType="Event Times"
        itemName={selectedLabels}
        itemDescription={
          selectedAttendeeCount > 0 ? STRINGS.ATTENDEES_WILL_BE_NOTIFIED(selectedAttendeeCount) : undefined
        }
      />
    </Drawer>
  );
};
